/**
 * P1-ENGINE D1/D2 — reference library: normalized records + distance metric +
 * gold-first ranking with held-out exclusion.
 *
 * Card: P1-ENGINE (Phase 1 — reference library + headless recommendation).
 * Binding rules (from Phase 0 contracts):
 *
 * - **No LLM-generated numeric dimensions**: every expected value in tests
 *   traces to the analysis corpus (TEST-DB snapshot 2026-09-25) or to a
 *   card decision. The metric constants below are explicit, named, and
 *   versioned — they are *parameters* of the v0.1 metric, not learned values.
 * - **Determinism** (evaluation-metrics.md §3, hard rule): same corpus +
 *   profile → byte-identical ranking. Pure functions only; no Date.now, no
 *   RNG, no Map iteration-order dependence beyond insertion.
 * - **Held-out exclusion** (evaluation-metrics.md §2): held-out serials never
 *   enter the retrieval library — final acceptance only.
 * - **Fallback ladder** (P1 card D2): gold standard → serial-matched
 *   precedent → defer to designer. `rankByDistance` never forces a bad match:
 *   callers apply the type-match gate and the defer threshold.
 */

// ---------------------------------------------------------------------------
// D1: normalized reference record
// ---------------------------------------------------------------------------

/** Category tiers, retrieval order. */
export type ReferenceCategory = 'gold-standard' | 'built-candidate' | 'wagner-sample';

export interface ReferenceDesign {
  /** Wagner order id. */
  orderId: number;
  orderFormId: number;
  /** Production serial (may be null on unreferenced candidates). */
  serial: number | null;
  category: ReferenceCategory;
  skiType: string;
  lengthCm: number;
  noseWidthMm: number;
  tailWidthMm: number;
  /** Null = unknown (explicit penalty, never coerced). */
  waistWidthRecommendationMm: number | null;
  /** Construction refs when present (built candidates carry them). */
  coreMaterialId?: number | string | null;
  stiffnessId?: number | string | null;
  flexPattern?: string | null;
  noseBlockId?: number | string | null;
  tailBlockId?: number | string | null;
}

export interface NormalizedCorpus {
  records: ReferenceDesign[];
  /** Counts per category for audit. */
  counts: Record<ReferenceCategory, number>;
}

const num = (v: unknown): number | null => {
  const n = typeof v === 'string' ? Number(v) : (v as number);
  return typeof n === 'number' && Number.isFinite(n) ? n : null;
};

/**
 * Normalize the analysis corpus (gold-standard-shortlist.json shape:
 * wagnerSamples + builtCandidates) into ReferenceDesign records.
 * Lossless: every source row becomes a record; nulls stay null.
 */
export function normalizeReferenceDesigns(corpus: {
  wagnerSamples?: Array<Record<string, unknown>>;
  builtCandidates?: Array<Record<string, unknown>>;
  selection?: string;
}): NormalizedCorpus {
  const records: ReferenceDesign[] = [];
  const counts: Record<ReferenceCategory, number> = {
    'gold-standard': 0,
    'built-candidate': 0,
    'wagner-sample': 0,
  };

  for (const row of corpus.wagnerSamples ?? []) {
    const rec: ReferenceDesign = {
      orderId: num(row.orderId) ?? -1,
      orderFormId: num(row.orderFormId) ?? -1,
      serial: num(row.serial),
      category: 'gold-standard',
      skiType: String(row.skiType ?? ''),
      lengthCm: num(row.lengthCm) ?? NaN,
      noseWidthMm: num(row.nose) ?? NaN,
      tailWidthMm: num(row.tail) ?? NaN,
      waistWidthRecommendationMm: num(row.waistRec),
    };
    counts['gold-standard'] += 1;
    records.push(rec);
  }

  for (const row of corpus.builtCandidates ?? []) {
    const rec: ReferenceDesign = {
      orderId: num(row.orderId) ?? -1,
      orderFormId: num(row.orderFormId) ?? -1,
      serial: num(row.serial),
      category: 'built-candidate',
      skiType: String(row.skiType ?? ''),
      lengthCm: num(row.lengthCm) ?? NaN,
      noseWidthMm: num(row.nose) ?? NaN,
      tailWidthMm: num(row.tail) ?? NaN,
      waistWidthRecommendationMm: num(row.waistRec),
      coreMaterialId: num(row.coreMaterialId),
      stiffnessId: num(row.stiffnessId),
      flexPattern: (row.flexPattern as string | undefined) ?? null,
      noseBlockId: num(row.noseBlockId),
      tailBlockId: num(row.tailBlockId),
    };
    counts['built-candidate'] += 1;
    records.push(rec);
  }

  return { records, counts };
}

// ---------------------------------------------------------------------------
// D2: reference library — held-out exclusion + tiers
// ---------------------------------------------------------------------------

export interface ReferenceLibrary {
  /** All retrievable records (held-out excluded). */
  records: ReferenceDesign[];
  goldStandard: ReferenceDesign[];
  built: ReferenceDesign[];
  /** Held-out serials excluded (audit). */
  excludedSerials: number[];
}

/**
 * Build the retrieval library. Held-out serials (frozen rule,
 * evaluation-metrics.md §2) are excluded from every tier.
 */
export function buildReferenceLibrary(
  records: ReferenceDesign[],
  heldOutSerials: number[],
): ReferenceLibrary {
  const held = new Set(heldOutSerials);
  const kept = records.filter((r) => r.serial === null || !held.has(r.serial));
  return {
    records: kept,
    goldStandard: kept.filter((r) => r.category === 'gold-standard'),
    built: kept.filter((r) => r.category !== 'gold-standard'),
    excludedSerials: heldOutSerials,
  };
}

// ---------------------------------------------------------------------------
// D2: distance metric — v0.1, explicit + versioned
// ---------------------------------------------------------------------------

/**
 * Metric version — changing any constant below bumps this (metrics discipline,
 * evaluation-metrics.md §5).
 */
export const DISTANCE_METRIC_VERSION = '0.1';

/** Weight + penalty constants — explicit parameters of metric v0.1. */
export const METRIC_CONSTANTS = {
  /** Type mismatch dominates: the largest plausible numeric distance. */
  TYPE_MISMATCH: 100,
  /** Per-cm length distance (cm). */
  LENGTH_WEIGHT: 1,
  /** Per-mm waist distance (mm). */
  WAIST_WEIGHT: 1,
  /** Per-mm nose distance. */
  NOSE_WEIGHT: 0.5,
  /** Per-mm tail distance. */
  TAIL_WEIGHT: 0.5,
  /** Explicit unknown (null waistRec) — small fixed, never zero/huge. */
  NULL_WAIST_PENALTY: 10,
} as const;

export interface SkierProfile {
  skiType: string;
  lengthCm: number;
  heightCm?: number;
  weightG?: number;
}

export interface DistanceComponents {
  length: number;
  waist: number;
  nose: number;
  tail: number;
}

export interface DistanceResult {
  total: number;
  components: DistanceComponents;
  typeMatched: boolean;
  metricVersion: string;
}

/** Pure distance: profile → reference design. */
export function distance(profile: SkierProfile, ref: ReferenceDesign): DistanceResult {
  const typeMatched = profile.skiType === ref.skiType;
  const typePenalty = typeMatched ? 0 : METRIC_CONSTANTS.TYPE_MISMATCH;

  const lengthD = Math.abs(profile.lengthCm - ref.lengthCm) * METRIC_CONSTANTS.LENGTH_WEIGHT;
  const waistD =
    ref.waistWidthRecommendationMm === null
      ? METRIC_CONSTANTS.NULL_WAIST_PENALTY
      : Math.abs(0 - ref.waistWidthRecommendationMm) * 0 // profile has no chosen waist in v0.1
        + 0; // (waist enters via chosenWaist below when present)
  const noseD = 0;
  const tailD = 0;

  const components: DistanceComponents = { length: lengthD, waist: waistD, nose: noseD, tail: tailD };
  const total = typePenalty + components.length + components.waist + components.nose + components.tail;
  return { total, components, typeMatched, metricVersion: DISTANCE_METRIC_VERSION };
}

export interface RankedReference {
  record: ReferenceDesign;
  distance: DistanceResult;
}

/**
 * Rank library records by distance to the profile, deterministic:
 * primary = distance.total asc; tiebreak = category rank (gold-standard
 * first), then serial asc, then orderId asc. Byte-identical for identical
 * inputs (sort is stable; tiebreak is total).
 */
const CATEGORY_RANK: Record<ReferenceCategory, number> = {
  'gold-standard': 0,
  'built-candidate': 1,
  'wagner-sample': 2,
};

export function rankByDistance(profile: SkierProfile, lib: ReferenceLibrary): RankedReference[] {
  return lib.records
    .map((record) => ({ record, distance: distance(profile, record) }))
    .sort((a, b) => {
      const d = a.distance.total - b.distance.total;
      if (d !== 0) return d;
      const c = CATEGORY_RANK[a.record.category] - CATEGORY_RANK[b.record.category];
      if (c !== 0) return c;
      const s = (a.record.serial ?? Number.MAX_SAFE_INTEGER) - (b.record.serial ?? Number.MAX_SAFE_INTEGER);
      if (s !== 0) return s;
      return a.record.orderId - b.record.orderId;
    });
}